import { useState, useRef, useEffect } from 'react';
import { Header } from './components/Header';
import { Homepage } from './components/Homepage';
import { InvestorPage } from './components/InvestorPage';
import { DealershipPage } from './components/DealershipPage';
import { Footer } from './components/Footer';
import { ScrollManager } from './components/ScrollManager';
import { Toaster } from './components/ui/sonner';

// Map URL paths to page names
const pathToPage: Record<string, string> = {
  '/': 'home',
  '/home': 'home',
  '/investors': 'investors',
  '/dealerships': 'dealerships',
};

// Map page names to URL paths
const pageToPath: Record<string, string> = {
  'home': '/',
  'investors': '/investors',
  'dealerships': '/dealerships',
};

// Get page name from current URL
function getPageFromPath(): string {
  const path = window.location.pathname;
  return pathToPage[path] || 'home';
}

// Get URL path from page name
function getPathFromPage(page: string): string {
  return pageToPath[page] || '/';
}

export default function App() {
  const [currentPage, setCurrentPage] = useState(() => getPageFromPath());
  const previousPageRef = useRef<string | null>(null);
  const isNavigatingRef = useRef(false);

  // Enhanced setCurrentPage that tracks previous page and updates URL
  const handlePageChange = (newPage: string) => {
    if (newPage !== currentPage) {
      isNavigatingRef.current = true;
      previousPageRef.current = currentPage;
      
      // Update URL without page reload
      const newPath = getPathFromPage(newPage);
      window.history.pushState({ page: newPage }, '', newPath);
      
      setCurrentPage(newPage);
      
      // Reset flag after state update
      setTimeout(() => {
        isNavigatingRef.current = false;
      }, 0);
    }
  };

  // Initialize URL state and set up history listener on mount
  useEffect(() => {
    // Initialize history state with current page
    const initialPage = getPageFromPath();
    window.history.replaceState({ page: initialPage }, '', window.location.pathname);
    
    // Handle browser back/forward buttons
    const handlePopState = (event: PopStateEvent) => {
      // Get the page from the URL or event state
      const page = event.state?.page || getPageFromPath();
      
      // Use a ref to get the current page value without dependency
      setCurrentPage((prevPage) => {
        if (page && page !== prevPage) {
          isNavigatingRef.current = true;
          previousPageRef.current = prevPage;
          
          setTimeout(() => {
            isNavigatingRef.current = false;
          }, 0);
          
          return page;
        }
        return prevPage;
      });
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []); // Only run once on mount

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <ScrollManager 
        currentPage={currentPage} 
        previousPage={previousPageRef.current} 
      />
      <Header currentPage={currentPage} setCurrentPage={handlePageChange} />
      <main className="flex-1">
        {currentPage === 'home' && <Homepage setCurrentPage={handlePageChange} />}
        {currentPage === 'investors' && <InvestorPage setCurrentPage={handlePageChange} />}
        {currentPage === 'dealerships' && <DealershipPage setCurrentPage={handlePageChange} />}
      </main>
      <Footer setCurrentPage={handlePageChange} />
      <Toaster />
    </div>
  );
}